<script setup>
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';

import cortexLogo from 'dashboard/assets/images/cx-cortex.png';
import NavbarAccountSwitcher from './NavbarAccountSwitcher.vue';
import NavbarNotificationBell from './NavbarNotificationBell.vue';
import NavbarBreadcrumb from './NavbarBreadcrumb.vue';
import NavbarProfileMenu from './NavbarProfileMenu.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import DialerLauncher from 'dashboard/components-next/dialer/DialerLauncher.vue';
import AgentPauseChip from 'dashboard/components-next/agent-pause/AgentPauseChip.vue';
import MassivaAlertChip from 'dashboard/components-next/massiva/MassivaAlertChip.vue';
import PauseReasonDialog from 'dashboard/components-next/agent-pause/PauseReasonDialog.vue';
import { useAgentPauseSync } from 'dashboard/components-next/agent-pause/useAgentPauseSync';
import WhatsAppSipDialerLauncher from 'dashboard/components-next/dialer/WhatsAppSipDialerLauncher.vue';
import Button from 'next/button/Button.vue';

const emit = defineEmits(['showCreateAccountModal', 'openKeyShortcutModal']);

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const searchShortcut = useKbd([`$mod`, 'k']);
// A barra superior está em toda tela do dashboard: é daqui que a pausa do
// agente (motivos da conta + pausa aberta) se mantém em dia.
useAgentPauseSync();

const goToNotifications = () => {
  router.push(accountScopedRoute('notifications_index'));
};
</script>

<template>
  <!-- Layout 3-col: search ocupa a coluna do meio (auto-width) e fica
       absolutamente centralizado no header. Esquerda e direita crescem
       em 1fr cada — quando o conteudo lateral encosta na search, o
       overflow lateral apenas trunca (min-w-0). -->
  <header
    class="grid grid-cols-[1fr_auto_1fr] items-center flex-shrink-0 h-14 border-b bg-n-solid-2 border-n-weak"
  >
    <!-- Esquerda: Logo + AccountSwitcher + Breadcrumb -->
    <div class="flex items-center min-w-0">
      <RouterLink
        :to="accountScopedRoute('home')"
        :aria-label="t('SIDEBAR.HOME')"
        class="flex items-center justify-center w-14 flex-shrink-0"
      >
        <img :src="cortexLogo" alt="Cortex" class="rounded-md size-8" />
      </RouterLink>
      <div
        class="flex items-center gap-2 flex-shrink-0 min-w-0 max-w-[240px] pl-1"
      >
        <div class="flex-shrink-0 w-px h-4 bg-n-strong" />
        <NavbarAccountSwitcher
          class="min-w-0"
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>
      <div class="flex items-center flex-1 min-w-0 px-3">
        <NavbarBreadcrumb />
      </div>
    </div>

    <!-- Centro: Search (sempre centralizado no header) -->
    <div class="flex items-center justify-center px-3">
      <RouterLink
        :to="{ name: 'search' }"
        class="hidden sm:flex items-center gap-2 h-8 px-2.5 rounded-lg outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30 hover:bg-n-solid-4 transition-colors min-w-[260px]"
      >
        <span class="flex-shrink-0 i-lucide-search size-3.5 text-n-slate-11" />
        <span class="flex-grow text-left text-sm text-n-slate-11">
          {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
        </span>
        <span
          class="tracking-wide pointer-events-none select-none text-n-slate-10 text-xs"
        >
          {{ searchShortcut }}
        </span>
      </RouterLink>
      <RouterLink
        :to="{ name: 'search' }"
        class="sm:hidden flex items-center justify-center size-8 rounded-lg hover:bg-n-alpha-1"
      >
        <span class="i-lucide-search size-4 text-n-slate-11" />
      </RouterLink>
    </div>

    <!-- Direita: Massiva + Pausa + Nova conversa + Bell + Profile -->
    <div class="flex items-center justify-end min-w-0 gap-1 pr-3">
      <MassivaAlertChip />
      <AgentPauseChip />
      <DialerLauncher />
      <WhatsAppSipDialerLauncher />
      <ComposeConversation>
        <template #trigger="{ toggle }">
          <Button
            icon="i-lucide-plus"
            :label="t('NEW_CONVERSATION.TITLE')"
            color="blue"
            size="sm"
            class="!h-8"
            @click="toggle"
          />
        </template>
      </ComposeConversation>
      <NavbarNotificationBell @open-notification-panel="goToNotifications" />
      <NavbarProfileMenu
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
      <PauseReasonDialog />
    </div>
  </header>
</template>
